import { define } from "@chatbotgang/etude/util/define";
import AdminPanelSettingsOutlined from "@mui/icons-material/AdminPanelSettingsOutlined";
import CableOutlined from "@mui/icons-material/CableOutlined";
import ChatBubbleOutlineOutlined from "@mui/icons-material/ChatBubbleOutlineOutlined";
import CodeOutlined from "@mui/icons-material/CodeOutlined";
import CreditCardOutlined from "@mui/icons-material/CreditCardOutlined";
import DescriptionOutlined from "@mui/icons-material/DescriptionOutlined";
import HomeOutlined from "@mui/icons-material/HomeOutlined";
import MemoryOutlined from "@mui/icons-material/MemoryOutlined";
import PeopleAltOutlined from "@mui/icons-material/PeopleAltOutlined";
import ScienceOutlined from "@mui/icons-material/ScienceOutlined";
import Box from "@mui/material/Box";
import type { SxProps } from "@mui/material/styles";
import type { muiTheme } from "@polifonia/theme";
import { theme } from "@polifonia/theme";
import { fakeT } from "@polifonia/utils/react/fakeT";
import { useStatsigClient } from "@statsig/react-bindings";
import { Link, useRouterState } from "@tanstack/react-router";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { Badge } from "@/components/Badge";
import { Tooltip } from "@/components/Tooltip";
import { useLowBalance } from "@/features/billing/useLowBalance";
import { countChannelsNeedingAttention } from "@/features/channels/channelAttention";
import { useOrgChannels } from "@/features/channels/useOrgChannels";
import type { FeatureFlagTypes } from "@/features/featureFlag";
import { useGetFeatureFlag } from "@/features/featureFlag";
import { findSelectedPath } from "@/layout/MainContent/findSelectedPath";
import type { MenuGroup } from "@/layout/MainContent/groupMenuItems";
import { groupMenuItems } from "@/layout/MainContent/groupMenuItems";

const styles = {
  list: {
    display: "flex",
    flexDirection: "column",
    gap: "2px",
    margin: "0px",
    padding: "4px 12px 12px",
    listStyle: "none",
  },
  compactList: {
    alignItems: "center",
    padding: "4px 0px 12px",
  },
  group: {
    display: "flex",
    flexDirection: "column",
    gap: "2px",
    margin: "0px",
    padding: "0px",
    listStyle: "none",
  },
  groupLabel: {
    padding: "18px 12px 6px",
    fontSize: "0.6875rem",
    fontWeight: 600,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    color: theme.colors.staticFgNote,
  },
  divider: {
    display: "block",
    width: "24px",
    height: "1px",
    margin: "6px auto",
    backgroundColor: theme.colors.staticFgLine,
  },
  item: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    minHeight: "36px",
    padding: "6px 12px",
    boxSizing: "border-box",
    outline: "none",
    "&:focus-visible": {
      outline: `2px solid ${theme.colors.blue060}`,
      outlineOffset: "-2px",
    },
    borderRadius: "6px",
    color: theme.colors.staticFgBody,
    fontSize: "0.875rem",
    textDecoration: "none",
    "&:hover": {
      backgroundColor: theme.colors.neutral010,
    },
  },
  compactItem: {
    position: "relative",
    justifyContent: "center",
    width: "44px",
    height: "44px",
    padding: "0px",
    borderRadius: "8px",
  },
  selectedItem: {
    backgroundColor: theme.colors.blue020,
    color: theme.colors.blue060,
    fontWeight: 500,
    "&:hover": {
      backgroundColor: theme.colors.blue020,
    },
  },
  iconSlot: {
    display: "inline-flex",
    "& svg": {
      fontSize: "1.125rem",
      color: theme.colors.staticFgNote,
    },
  },
  selectedIconSlot: {
    "& svg": {
      fontSize: "1.125rem",
      color: theme.colors.blue060,
    },
  },
  labelBlock: {
    flex: 1,
    minWidth: 0,
    display: "flex",
    flexDirection: "column",
  },
  note: {
    fontSize: "0.75rem",
    fontWeight: 500,
    lineHeight: 1.3,
    color: theme.colors.staticFgNegative,
  },
  label: {
    flex: 1,
    minWidth: 0,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  dot: {
    position: "absolute",
    top: "9px",
    right: "9px",
    width: "8px",
    height: "8px",
    borderRadius: "999px",
    backgroundColor: theme.colors.red060,
    border: `2px solid ${theme.colors.white}`,
  },
} satisfies Record<string, SxProps<typeof muiTheme>>;

/** A live alert on a menu item; `description` names it when only a dot shows. */
interface MenuAlert {
  /** A short count, shown at the end of the row. */
  badge?: React.ReactNode;
  /** A status shown in red under the label, so the label is never cut off. */
  note?: string;
  description: string;
}

interface DrawerMenuListItemProps {
  path: string;
  label: string;
  icon: React.ReactNode;
  isBeta: boolean;
  isNew: boolean;
  alert: MenuAlert | undefined;
  selected: boolean;
  compact: boolean;
}

const DrawerMenuListItem: React.FC<DrawerMenuListItemProps> =
  function DrawerMenuListItem({
    path,
    label,
    icon,
    isBeta,
    isNew,
    alert,
    selected,
    compact,
  }) {
    const { t } = useTranslation();
    const link = (
      <Box
        component={Link}
        to={path}
        aria-current={selected ? "page" : undefined}
        aria-label={
          compact
            ? [label, alert?.description].filter(Boolean).join(", ")
            : undefined
        }
        sx={{
          ...styles.item,
          ...(compact && styles.compactItem),
          ...(selected && styles.selectedItem),
        }}
      >
        <Box
          component="span"
          sx={{ ...styles.iconSlot, ...(selected && styles.selectedIconSlot) }}
        >
          {icon}
        </Box>
        {compact ? (
          alert ? (
            <Box component="span" sx={styles.dot} />
          ) : null
        ) : (
          <>
            <Box component="span" sx={styles.labelBlock}>
              <Box component="span" sx={styles.label}>
                {label}
              </Box>
              {alert?.note ? (
                <Box component="span" sx={styles.note}>
                  {alert.note}
                </Box>
              ) : null}
            </Box>
            {isBeta ? <Badge variant="primary">Beta</Badge> : null}
            {isNew ? (
              <Badge variant="primary">{t("sideMenu.badge.new", "New")}</Badge>
            ) : null}
            {alert?.badge}
          </>
        )}
      </Box>
    );
    return (
      <Box component="li" sx={{ display: "flex", flexDirection: "column" }}>
        {compact ? (
          <Tooltip title={label} placement="right">
            {link}
          </Tooltip>
        ) : (
          link
        )}
      </Box>
    );
  };

const t = fakeT;

const menuItems = {
  overview: {
    icon: <HomeOutlined />,
    path: "/",
    labelTranslationKey: t("sideMenu.overview"),
    labelFallback: "Overview",
  } as const,
  dataHub: {
    icon: <PeopleAltOutlined />,
    path: "/data-hub",
    labelTranslationKey: t("dataHub.page.title"),
    labelFallback: "Profile unification",
    // The settings page that moved into Profile unification redirects there.
    activePaths: ["/applications/cdh"],
  } as const,
  applications: {
    icon: <CableOutlined />,
    path: "/applications",
    labelTranslationKey: t("applications.dataIntegrations.title"),
    labelFallback: "Data & integrations",
  } as const,
  billing: {
    icon: <CreditCardOutlined />,
    path: "/billing",
    labelTranslationKey: t("billing.usage.title"),
    labelFallback: "Billing & usage",
  } as const,
  channels: {
    icon: <ChatBubbleOutlineOutlined />,
    path: "/channels",
    labelTranslationKey: t("sideMenu.channels"),
  } as const,
  openApi: {
    icon: <CodeOutlined />,
    // The path is a cross-repo contract: MAAC links to `applications/api-keys`.
    path: "/applications/api-keys",
    labelTranslationKey: "OpenAPI",
  } as const,
  mcp: {
    icon: <MemoryOutlined />,
    path: "/applications/mcp-connections",
    labelTranslationKey: "MCP",
    isNew: true,
  } as const,
  contractCenter: {
    icon: <DescriptionOutlined />,
    path: "/contract-center",
    labelTranslationKey: t("sideMenu.contractCenter"),
    // Shown until the translations are published, so the item does not read
    // as its own key. Once published, the key resolves and this is unused —
    // no edit needed. zh/ja/th see English in the meantime, which is why the
    // feature stays gated to the internal org until then.
    labelFallback: "Contract Center",
  } as const,
  userManagement: {
    icon: <AdminPanelSettingsOutlined />,
    path: "/user-management",
    labelTranslationKey: t("userManagement.productAccess.title"),
    labelFallback: "Product access",
  } as const,
  vibeLab: {
    icon: <ScienceOutlined />,
    path: "/vibe-lab",
    labelTranslationKey: "CL Vibe Lab",
    beta: true,
  } as const,
} satisfies Record<
  | "overview"
  | "dataHub"
  | "applications"
  | "billing"
  | "channels"
  | "openApi"
  | "mcp"
  | "contractCenter"
  | "userManagement"
  | "vibeLab",
  {
    icon: React.ReactNode;
    path: string;
    labelTranslationKey: string;
    /** Text to show while `labelTranslationKey` has no published translation. */
    labelFallback?: string;
    beta?: boolean;
    /** Marks a feature to call out until it is no longer new. */
    isNew?: boolean;
    /** Other path prefixes that also select this item. */
    activePaths?: ReadonlyArray<string>;
  }
>;

const drawerItems = define<
  Array<{
    path: keyof typeof menuItems;
    group: MenuGroup;
    requireFeatureFlag?: Array<FeatureFlagTypes["ToggleKey"]>;
    requireCheckGate?: Array<string>;
  }>
>([
  {
    path: "overview",
    group: "home",
  },
  {
    path: "dataHub",
    group: "customerData",
  },
  {
    path: "channels",
    group: "connect",
  },
  {
    path: "applications",
    group: "connect",
  },
  {
    path: "openApi",
    group: "developer",
    requireFeatureFlag: ["enable-openapi-v2-key-self-serve"],
    requireCheckGate: ["enable_openapi_v2_key_self_serve"],
  },
  {
    // Shown to every org so they know Crescendo Lab MCP exists; orgs without
    // it see the page's "not enabled" notice with a contact link.
    path: "mcp",
    group: "developer",
  },
  {
    path: "userManagement",
    group: "organization",
    requireCheckGate: ["user_management"],
  },
  {
    path: "billing",
    group: "organization",
  },
  {
    path: "contractCenter",
    group: "organization",
    requireCheckGate: ["enable_contract_signing"],
  },
  {
    path: "vibeLab",
    group: "lab",
    requireFeatureFlag: ["enable-vibe-lab"],
    requireCheckGate: ["enable_vibe_lab"],
  },
]);

/**
 * Alerts backed by live data: how many channels need attention, and whether
 * the balance is at or below its alert threshold. Nothing shows until the data
 * has loaded.
 */
const useMenuAlerts = (): Partial<Record<string, MenuAlert>> => {
  const { t } = useTranslation();
  const orgChannels = useOrgChannels();
  const lowBalance = useLowBalance();
  const channelsNeedingAttention = orgChannels.isSuccess
    ? countChannelsNeedingAttention(orgChannels.channels)
    : 0;
  return useMemo(() => {
    const alerts: Partial<Record<string, MenuAlert>> = {};
    if (channelsNeedingAttention > 0) {
      alerts[menuItems.channels.path] = {
        badge: <Badge variant="attentive">{channelsNeedingAttention}</Badge>,
        description: t("overview.attention.channels", {
          count: channelsNeedingAttention,
        }),
      };
    }
    if (lowBalance) {
      const text = t("sideMenu.badge.lowBalance", "Low balance");
      alerts[menuItems.billing.path] = {
        note: text,
        description: text,
      };
    }
    return alerts;
  }, [channelsNeedingAttention, lowBalance, t]);
};

interface DrawerMenuListProps {
  /** Icons only, with each label in a tooltip. */
  compact: boolean;
}

const DrawerMenuList: React.FC<DrawerMenuListProps> = function DrawerMenuList({
  compact,
}) {
  const { t } = useTranslation();
  const client = useStatsigClient();
  const getFeatureFlag = useGetFeatureFlag();
  const items = useMemo(() => {
    return drawerItems
      .filter(function checkPermission(route) {
        if (!route.requireFeatureFlag && !route.requireCheckGate) return true;
        return (
          route.requireCheckGate?.some((key) => client.checkGate(key)) ||
          route.requireFeatureFlag?.some(getFeatureFlag)
        );
      })
      .map((route) => {
        const menuItem = menuItems[route.path];
        return {
          path: menuItem.path,
          group: route.group,
          icon: menuItem.icon,
          label:
            "labelFallback" in menuItem
              ? t(menuItem.labelTranslationKey, menuItem.labelFallback)
              : t(menuItem.labelTranslationKey),
          isBeta: "beta" in menuItem ? menuItem.beta : false,
          isNew: "isNew" in menuItem ? menuItem.isNew : false,
          prefixes: [
            menuItem.path,
            ...("activePaths" in menuItem ? menuItem.activePaths : []),
          ],
        };
      });
  }, [getFeatureFlag, client, t]);
  const alerts = useMenuAlerts();
  // The home group holds only Overview and needs no heading.
  const groupLabels: Record<MenuGroup, string | null> = useMemo(
    () => ({
      home: null,
      customerData: t("sideMenu.group.customerData", "Customer data"),
      connect: t("sideMenu.group.connect", "Connect"),
      developer: t("sideMenu.group.developer", "Developers"),
      organization: t("sideMenu.group.organization", "Organization"),
      lab: t("sideMenu.group.lab", "Labs"),
    }),
    [t],
  );
  const pathname = useRouterState({
    select: (state) => state.location.pathname,
  });
  // The most specific prefix wins, so `/applications/api-keys` selects
  // OpenAPI rather than Data & integrations.
  const selectedPath = useMemo(
    () => findSelectedPath(items, pathname),
    [items, pathname],
  );
  const sections = useMemo(() => groupMenuItems(items), [items]);
  return (
    <Box
      component="ul"
      sx={{ ...styles.list, ...(compact && styles.compactList) }}
    >
      {sections.map((section) => {
        const groupLabel = groupLabels[section.group];
        return (
          <Box component="li" key={section.group} sx={styles.group}>
            {groupLabel ? (
              compact ? (
                <Box component="span" sx={styles.divider} />
              ) : (
                <Box component="span" sx={styles.groupLabel}>
                  {groupLabel}
                </Box>
              )
            ) : null}
            <Box component="ul" sx={styles.group}>
              {section.items.map((item) => (
                <DrawerMenuListItem
                  key={item.path}
                  path={item.path}
                  label={item.label}
                  icon={item.icon}
                  isBeta={Boolean(item.isBeta)}
                  isNew={Boolean(item.isNew)}
                  alert={alerts[item.path]}
                  selected={item.path === selectedPath}
                  compact={compact}
                />
              ))}
            </Box>
          </Box>
        );
      })}
    </Box>
  );
};

export { DrawerMenuList };
